import { Field } from "formik";
import { TEmployeeRole } from "../../lib/types/employee_roles";
import { useEffect, useState } from "react";
import { Spinner } from "./spinner";
import { TSkills } from "../../lib/types/skills";

type TPositionsCheckListProps = {
    fieldName: string;
    roles: TEmployeeRole[];
};
type TPositionsCheckListNoFormikProps = {
    roles: TEmployeeRole[];
    checkedList: {
        [key: string]: boolean;
    };
    refs: {
        current: {
            [keys: string]: boolean;
        };
    };
    wideColumns?: boolean;
};

/**
 * To be found in sign up forms
 * @param props
 * @returns
 */
export const PositionsCheckList = (props: TPositionsCheckListProps) => {
    const { roles, fieldName } = props;

    return (
        <>
            {roles.map((pos) => {
                return (
                    <>
                        <div className="col-md-6 col-lg-4">
                            <div className="form-check">
                                <label className="form-control-label my-1">
                                    <Field
                                        name={fieldName}
                                        type="checkbox"
                                        data-testid="checkbox-value-formik"
                                        className="form-check-input"
                                        value={String(pos.id)}
                                    />
                                    {pos.name}
                                </label>
                            </div>
                        </div>
                    </>
                );
            })}
        </>
    );
};

/**
 * Used in dental-lab-info.
 * As a lab in My Account you can edit the positions you are looking for.
 * @param props
 * @returns
 */
export const PositionsCheckListNoFormik = (
    props: TPositionsCheckListNoFormikProps
) => {
    const { roles, checkedList, refs, wideColumns = false } = props;

    return (
        <>
            {roles.map((pos) => {
                return (
                    <div
                        className={
                            " " + (wideColumns ? " col-md-6" : "col-md-6")
                        }
                    >
                        <div className="input-group p-0 m-1 ">
                            <div className="">
                                <input
                                    type="checkbox"
                                    defaultChecked={checkedList[pos.id]}
                                    data-testid="checkbox-value-no-formik"
                                    className="form-check-input"
                                    key={pos.id}
                                    id={pos.id + pos.name}
                                    onChange={(e) => {
                                        refs.current[pos.id] = e.target.checked;
                                    }}
                                />

                                <label
                                    className="form-label p-0 ms-2"
                                    htmlFor={pos.id + pos.name}
                                >
                                    {pos.name}
                                </label>
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

type TPositionsCheckListEmployeeRoleBackOffice = {
    skillList: TSkills[];
    checkedList: Record<number, boolean>;
    isInModal: boolean;
    setRefSkills: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
    updateSkillName?: (posId: string, newName: string) => void;
};

/**
 * Used in backoffice Employee Roles Section
 * As an admin I can edit what skills are assigned to one employee role
 * @param props
 * @returns
 */
export const PositionsCheckListEmployeeRoleBackOffice = (
    props: TPositionsCheckListEmployeeRoleBackOffice
) => {
    const { skillList, checkedList, isInModal, setRefSkills, updateSkillName } =
        props;
    const [editingSkillId, setEditingSkillId] = useState<string | undefined>(
        undefined
    );
    const [editedSkillName, setEditedSkillName] = useState<string>("");
    const [savingNewSkillName, setSavingNewSkillName] = useState(false);

    const saveEditPosition = async (posId: string) => {
        if (!updateSkillName) return;
        const inputName = editedSkillName;
        if (!inputName) return;
        setSavingNewSkillName(true);
        await updateSkillName(posId, inputName);
        setSavingNewSkillName(false);
        setEditingSkillId(undefined);
    };

    useEffect(() => {
        setEditingSkillId(undefined);
    }, []);

    return (
        <>
            {skillList.map((skill) => (
                <div
                    key={`skill_id_row_editing_in_role_table_${skill.id}`}
                    className="row mb-2 "
                >
                    <div className="col mb-2">
                        {editingSkillId !== skill.guid && (
                            <>
                                {!isInModal && (
                                    <button
                                        className="btn btn-sm btn-light rounded-3 me-2"
                                        onClick={() => {
                                            setEditingSkillId(skill.guid);
                                            setEditedSkillName(skill.name);
                                        }}
                                    >
                                        <i className="fa-regular fa-pen-to-square fa-xs"></i>
                                    </button>
                                )}
                                <div className="form-check form-check-inline">
                                    <label className="form-check-label">
                                        <input
                                            type="checkbox"
                                            checked={
                                                checkedList[skill.id] || false
                                            }
                                            onChange={(e) =>
                                                setRefSkills((prev) => ({
                                                    ...prev,
                                                    [skill.id]:
                                                        e.target.checked,
                                                }))
                                            }
                                            className="form-check-input me-2"
                                        />
                                        {skill.name}
                                    </label>
                                </div>
                            </>
                        )}
                        {editingSkillId === skill.guid && (
                            <div className="input-group">
                                <button
                                    className="btn btn-sm btn-light rounded-3 me-2"
                                    disabled
                                >
                                    <i className="fa-regular fa-pen-to-square fa-xs"></i>
                                </button>
                                <input
                                    type="text"
                                    value={editedSkillName}
                                    onChange={(e) =>
                                        setEditedSkillName(e.target.value)
                                    }
                                    className="form-control rounded-3"
                                    placeholder="Skill Name"
                                />
                            </div>
                        )}
                    </div>

                    {editingSkillId === skill.guid && (
                        <>
                            <div className="col-lg-6">
                                <div className="alert alert-warning py-2 px-3 rounded-3 small">
                                    <small>
                                        <strong>
                                            <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                            Warning:
                                        </strong>{" "}
                                        Changing the name of this skill will
                                        affect the entire platform. Are you sure
                                        you want to continue?
                                    </small>
                                    <br />
                                    <div className="text-end">
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary ms-3 btn-sm"
                                            onClick={() =>
                                                saveEditPosition(editingSkillId)
                                            }
                                        >
                                            {!savingNewSkillName && (
                                                <span>Save</span>
                                            )}
                                            {savingNewSkillName && (
                                                <Spinner spinnerColor="text-primary" />
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger ms-3 btn-sm"
                                            onClick={() =>
                                                setEditingSkillId(undefined)
                                            }
                                            disabled={savingNewSkillName}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ))}
        </>
    );
};
